import { useState, useEffect, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { verifyAndActivateSubscription } from '../services/subscriptionVerification';

export function useSubscription(user) {
  const [subscriptionStatus, setSubscriptionStatus] = useState('free');
  const [subscriptionType, setSubscriptionType] = useState(null);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasTriedSync = useRef(false);

  useEffect(() => {
    if (!user) {
      setSubscriptionStatus('free');
      setSubscriptionType(null);
      setSubscriptionEndDate(null);
      setLoading(false);
      return;
    }

    // Check if user is admin (grant Coach access)
    const adminUids = process.env.REACT_APP_ADMIN_UIDS?.split(',') || [];
    if (adminUids.includes(user.uid)) {
      setSubscriptionStatus('active');
      setSubscriptionType('coach');
      setSubscriptionEndDate(null);
      setLoading(false);
      return;
    }

    // Listen to subscription changes in real-time
    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, async (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const status = data?.subscriptionStatus || 'free';
        const type = data?.subscriptionType || null;
        const endDate = data?.subscriptionEndDate?.toDate();

        console.log('📊 Subscription data:', { status, type, endDate, hasTriedSync: hasTriedSync.current });

        // Auto-sync: If user has active subscription but no type, sync from Stripe
        // This fixes users who subscribed before the webhook was fixed
        if (status === 'active' && !type && !hasTriedSync.current && user.email) {
          console.log('🔄 Detected incomplete subscription data, syncing from Stripe...');
          hasTriedSync.current = true;
          try {
            await verifyAndActivateSubscription(user.email, user.uid);
            console.log('✅ Subscription synced successfully');
            // The snapshot listener will pick up the changes automatically
          } catch (error) {
            console.error('❌ Failed to sync subscription:', error);
            // Continue with current data even if sync fails
          }
        }

        // Check if subscription is still valid
        if (status === 'active' && endDate && endDate > new Date()) {
          console.log('✅ Valid subscription detected:', type);
          setSubscriptionStatus('active');
          setSubscriptionType(type);
          setSubscriptionEndDate(endDate);
        } else {
          console.log('⚠️ No valid subscription:', { status, endDate, now: new Date() });
          setSubscriptionStatus('free');
          setSubscriptionType(null);
          setSubscriptionEndDate(null);
        }
      } else {
        setSubscriptionStatus('free');
        setSubscriptionType(null);
        setSubscriptionEndDate(null);
      }
      setLoading(false);
    }, (error) => {
      console.error('Error fetching subscription:', error);
      setSubscriptionStatus('free');
      setSubscriptionType(null);
      setSubscriptionEndDate(null);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const isPro = subscriptionStatus === 'active' && subscriptionType === 'pro';
  const isCoach = subscriptionStatus === 'active' && subscriptionType === 'coach';

  return { subscriptionStatus, subscriptionType, subscriptionEndDate, isPro, isCoach, loading };
}